"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  findInclusiveLanguageIssues,
  type InclusiveCategory,
  type InclusiveMatch,
} from "@/lib/tool-kit/writing-analysis/inclusive-language";

/** English strings. A translated content file overrides these through `ui`. */
export const INCLUSIVE_LANGUAGE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Throughout history, mankind has adapted to man-made environments. In this study, each participant completed the survey before he or she met the chairman of the review board. We compared elderly residents who suffer from dementia with a normal control group. Staff described several residents as wheelchair-bound, and the report noted that minorities were underrepresented. As one carer put it, “the elderly just need patience.”",
  enterPrompt:
    "Paste or type some text to check it against APA's bias-free language guidance.",
  results: "Results",
  terms: "Terms to review",
  categoriesFound: "Areas",
  quoted: "Left alone in quotations",
  judgement:
    "These are prompts, not verdicts. The right term depends on context and on what the people you describe call themselves — person-first and identity-first language differ between communities. Nothing here changes your text.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with terms to review highlighted",
  hoverHint:
    "Hover over a highlight to see alternatives; each term is explained below.",
  titleAlternatives: "Consider: {alternatives}",
  noneFound:
    "No terms from APA's lists found. Still read for the things no word list catches: unnecessary labels, assumptions and stereotypes.",
  listHeading: "Terms and alternatives",
  consider: "Consider:",
  times: "×{count}",
  categories: {
    gender: "Gender",
    age: "Age",
    disability: "Disability",
    health: "Health",
    race: "Race and ethnicity",
    identity: "Sexual orientation and gender identity",
    ses: "Socioeconomic status",
  },
  notes: {
    adjectiveLabel:
      "Turning an adjective into a label for a group (“the poor”, “the disabled”) reduces people to one characteristic. APA recommends wording that describes people.",
    caucasian:
      "APA discourages “Caucasian” because of its racist origins; use “White” or “European American”. (The Caucasus region and its languages are a separate, standard use.)",
    chair:
      "APA's guide recommends “chair” or “chairperson” over “chairman”. Keep the original in an official title or a quotation.",
    disabilityEuphemism:
      "APA lists euphemisms such as “special needs”, “differently abled” and “handicapped” as terms to avoid. Say “disabled” or “person with a disability”, as the people described prefer.",
    enslaved:
      "APA's guide uses “person who is enslaved” rather than “slave”, putting the person before the condition imposed on them. Established terms such as “the slave trade” remain standard.",
    eskimo:
      "“Eskimo” may be considered pejorative. Use the name a people uses for itself, such as Inuit, Yupik or Iñupiat.",
    firstYear:
      "Like other “-man” words, “freshman” assumes a male default; many universities now say “first-year student”. It is still an official term at some institutions, so keep it in names.",
    genericMan:
      "Generic “man” words treat the default human as male. APA lists terms such as “mankind” as problematic and prefers words that include everyone.",
    hearing:
      "Many Deaf people reject “hearing-impaired”. APA prefers “Deaf” for the cultural community or “hard of hearing”, as the person prefers.",
    heOrShe:
      "For a generic person, APA recommends singular “they”. Use “he or she” only when you know those pronouns fit everyone you describe.",
    homeless:
      "APA prefers “people experiencing homelessness”, which describes a situation rather than an identity.",
    homosexual:
      "APA calls “homosexual” outdated and says not to use it for a person's sexual orientation. Use “gay”, “lesbian” or the term people use for themselves.",
    incarcerated:
      "APA's guide replaces “prisoner” and “convict” with person-first wording such as “person who is incarcerated”. Established terms such as “prisoners of war” stay.",
    intellectualDisability:
      "“Mentally retarded” is outdated and stigmatizing. Use “intellectual disability” or “person with an intellectual disability”.",
    layperson:
      "“Layperson” or “nonspecialist” includes everyone; for “in layman's terms”, try “in plain language”.",
    mentalHealthTerms:
      "APA's guide lists terms such as “mentally ill”, “crazy” and “insane” as stigmatizing. Name the condition (“person with a mental illness”) or describe the behavior.",
    minorities:
      "APA's guide advises against “minority” for racial and ethnic groups. Name the groups, or use broader terms such as “people of color” or “historically marginalized communities”. (“Sexual and gender minorities” remains an accepted umbrella term.)",
    nonWhite:
      "“Non-White” defines people by what they are not and treats White as the default. Name the groups, or use “people of color” where a broad term is needed.",
    normal:
      "Calling a comparison group “normal” implies the other group is abnormal. APA recommends “nondisabled” or “people without disabilities” — or name the condition the group does not have.",
    occupation:
      "Gendered job titles assume who does the job. APA recommends gender-neutral occupational terms: “police officer”, not “policeman”.",
    olderAdults:
      "APA prefers “older adults” or a stated age range (“adults aged 65 and older”) to “the elderly”, “elderly people” or “the aged”.",
    oppositeSex:
      "“The opposite sex” assumes there are only two. Name the groups you mean, or write “another sex” or “a different gender”.",
    oriental:
      "“Oriental” is pejorative when it describes people. Name the nation or region (“Korean American”, “East Asian”). It survives in some object names, such as rugs.",
    seniors:
      "APA lists “seniors” and “senior citizens” among terms to avoid for older people. (Students in their final year are a different “seniors”, and that use is fine.)",
    sexualPreference:
      "“Sexual preference” implies a choice. APA uses “sexual orientation”.",
    sexWork:
      "APA's guide uses “sex worker” or “person who engages in sex work” rather than “prostitute”.",
    shortStature:
      "“Midget” is a slur. APA's guide follows Little People of America: “little person”, “person of short stature” or “person with dwarfism”.",
    slashPronoun:
      "Slash forms such as “he/she” assume a gender binary, and APA advises against “s/he” and “(s)he” outright. Use singular “they” for a generic person.",
    substanceUse:
      "APA's guide replaces labels such as “addict” and “alcoholic” with person-first wording: “person with a substance use disorder”, “person who uses drugs”.",
    suffers:
      "“Suffers from” and “afflicted with” cast a condition as a tragedy. APA's guide prefers neutral wording such as “has” or “lives with”.",
    suicide:
      "“Committed” suggests a crime, and “successful” or “failed” score a death. APA's guide prefers “died by suicide” and “survived a suicide attempt”.",
    thirdWorld:
      "APA's guide notes that “Third World” is no longer an accurate way to group countries. Name the countries, or use terms such as “low- and middle-income countries”.",
    transgender:
      "“Transgender” is an adjective: “a transgender woman”, “transgender people”. APA's guide calls “transgendered” dated.",
    victim:
      "APA lists “stroke victim” as problematic. Put the person first: “a person who has had a stroke”, “a person with HIV”.",
    wheelchair:
      "“Wheelchair-bound” and “confined to a wheelchair” cast a mobility aid as a prison. APA's guide prefers “wheelchair user” or “person who uses a wheelchair”.",
  },
} as const;

type Strings = typeof INCLUSIVE_LANGUAGE_CHECKER_STRINGS;

const TONES: Record<InclusiveCategory, HighlightTone> = {
  gender: "violet",
  age: "amber",
  disability: "sky",
  health: "emerald",
  race: "rose",
  identity: "violet",
  ses: "amber",
};

interface Group {
  key: string;
  first: InclusiveMatch;
  count: number;
}

function groupMatches(matches: readonly InclusiveMatch[]): Group[] {
  const groups = new Map<string, Group>();
  for (const match of matches) {
    const key = `${match.note}|${match.text.toLowerCase()}`;
    const group = groups.get(key);
    if (group) {
      group.count += 1;
    } else {
      groups.set(key, { key, first: match, count: 1 });
    }
  }
  return [...groups.values()];
}

function TermCard({
  group,
  strings,
  fmt,
}: {
  group: Group;
  strings: Strings;
  fmt: (value: number) => string;
}) {
  const match = group.first;
  return (
    <li className={`${CARD} space-y-2`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={HIGHLIGHT_TONES[TONES[match.category]]}>
          {match.text}
        </span>
        {group.count > 1 ? (
          <span className="text-muted-foreground text-xs">
            {t(strings.times, { count: fmt(group.count) })}
          </span>
        ) : null}
        <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground text-xs">
          {strings.categories[match.category]}
        </span>
      </div>
      <p className="text-foreground text-sm">
        <span className="font-medium">{strings.consider}</span>{" "}
        {match.alternatives.join(", ")}
      </p>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.notes[match.note]}
      </p>
    </li>
  );
}

export default function InclusiveLanguageCheckerWidget() {
  const strings = useToolUi(INCLUSIVE_LANGUAGE_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => findInclusiveLanguageIssues(deferred),
    [deferred]
  );

  const highlights: TextMatch[] = report.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.category,
    title: t(strings.titleAlternatives, {
      alternatives: match.alternatives.join(", "),
    }),
  }));
  const groups = groupMatches(report.matches);
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="il-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.terms}
                value={fmt(report.matches.length)}
              />
              <StatCard
                label={strings.categoriesFound}
                value={fmt(report.byCategory.length)}
              />
              <StatCard
                label={strings.quoted}
                value={fmt(report.quotedSkipped)}
              />
            </StatGrid>
          </div>
          <Notice>{strings.judgement}</Notice>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {groups.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.listHeading}</h3>
              <ul className="space-y-3">
                {groups.map((group) => (
                  <TermCard
                    fmt={fmt}
                    group={group}
                    key={group.key}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          )}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
